/**
 * All events created and subscribed to should be taken from this
 * map to ensure that naming stays consistent.  Anything added to this
 * list MUST be namespaced to prevent collisions:
 *
 * Ex. User events should prepend 'user': 'user:signed-out'
 *
 * This also allows for easier searching in logs. For easier
 * use in code, usee a titlecased version of the event name:
 *
 * 'UserSignedIn'
 */
export default {
  LayoutMounted: 'layout:mounted',
  PageViewed: 'page_viewed',

  /** Takes { feature: string, logo: string, }
   * fired when a user lands on stitch
   */
  StitchVisit: 'stitch:visit',

  /**
   * Fired when a user selects a Domain to purchase.
   * Takes { tld: string, name: string }
   */
  DomainSelected: 'domain:selected',

  /** Fired any time a modal is opened or closed.  { name: string } */
  ModalOpen: 'modal:open',
  ModalClose: 'modal:close',

  /** Fired when a user selects a logo to use.  Takes { logo: Logo } */
  LogoSaved: 'logo:saved',
  /** Fired when a user intends to share a logo.  Takes { logo: Logo } */
  LogoShareMenuOpened: 'logo:share-menu-opened',
  /** Fired when a user copies an email signature. Takes { slug: string } */
  EmailSignatureSaved: 'email-signature:saved',
  /** Fired when a user opens their favorites menu */
  FavoritesMenuOpened: 'favorites:menu-opened',

  /**
   * Fired any time a user navigates with the progress bar.
   * Takes { step: Step }
   */
  ProgressBarNavigated: 'progress-bar:navigated',

  /**
   * Fires when a progress steps transitions to the completed stage.
   * Takes ProgressStep
   */
  ProgressStepCompleted: 'progress:step-completed',
  /** Fires if all progress steps have been finished */
  AllProgressStepsCompleted: 'progress:all-steps-completed',

  /** Fires when a user downloads a file. { name: string } */
  FileDownloaded: 'file:downloaded',

  AppVisit: 'app:visit',

  /** Takes a User object returned from Amplify Auth methods */
  UserSignedIn: 'user:signed-in',
  /** Takes no parameters */
  UserSignedOut: 'user:signed-out',
  /** Takes a User object returned from Amplify Auth methods */
  UserRegistered: 'user:registered',
  /** Takes a User object returned from Amplify Auth methods */
  UserResetPassword: 'user:reset-password',
  /** Takes a User object returned from Amplify Auth methods */
  UserUpdatedAttributes: 'user:updated-attributes',
  /**
   * Accepts the type of modal to open & if the user can close
   * it as an Object
   */
  UserOpenAuthModal: 'user:open-auth-modal',

  NotificationClicked: 'notification:clicked',

  UserOpenDashboardUpgradeModal: 'user:open-dashboard-upgrade-modal',
  UserOpenPostSaveTourModal: 'user:open-post-save-tour-modal',
  UserOpenPostUpgradeModal: 'user:open-post-upgrade-modal',
  UserOpenOnePageSiteUpgradeModal: 'user:open-one-page-site-upgrade-modal',
  UserOpenOnePageSiteEditorUpgradeModal:
    'user:open-one-page-site-editor-upgrade-modal',
  UserOpenEmailSignatureUpgradeModal: 'user:open-email-signature-upgrade-modal',
  UserOpenPurchaseBusinessEmailModal: 'user:open-purchase-business-email-modal',

  ConfettiFire: 'confetti-fire',

  /** Accepts the Wordmark as a string */
  AppWordmarkUpdated: 'app:wordmark-updated',
  /** Accepts the Slogan as a string, or null */
  AppSloganUpdated: 'app:slogan-updated',
  /** Accepts the a search term as a string */
  AppWordCloudUpdated: 'app:word-cloud-updated',

  /** Accepts the Order */
  PurchaseCompleted: 'purchase:completed',
  /** Accepts the Order if available, or null otherwise */
  PurchaseFailed: 'purchase:failed',

  /** Enhanced flow events. */
  EnhancedFlowIndustrySelected: 'enhanced-flow:industry-selected',
  EnhancedFlowColorThemesSelected: 'enhanced-flow:color-themes-selected',
  EnhancedFlowFontStylesSelected: 'enhanced-flow:font-styles-selected',
  EnhancedFlowKeywordsSelected: 'enhanced-flow:keywords-selected',
  EnhancedFlowLogoTypeSelected: 'enhanced-flow:logo-type-selected',

  /* Enhanced Ecommerce events  */
  EcommerceImpressions: 'ecommerce:impressions',
  EcommerceDetailView: 'ecommerce:detail-view',
  EcommerceAddToCart: 'ecommerce:add-to-cart',
  EcommerceRemoveFromCart: 'ecommerce:remove-from-cart',
  EcommerceClicked: 'ecommerce:clicked',
  EcommerceCheckout: 'ecommerce:checkout',
  EcommercePurchase: 'ecommerce:purchase',
  EcommerceAddToWishlist: 'ecommerce:add-to-wishlist',
  EcommerceViewCart: 'ecommerce:view-cart',
  EcommerceSelectContent: 'ecommerce:select-content',

  /* Accepts a payload of { event: string, payload... } */
  AnalyticsGA4Event: 'analytics:ga4-event',
  /* Accepts a payload of {{ search_term: string, type: string }} */
  AnalyticsSearch: 'analytics:search',
  AnalyticsSelectLogo: 'analytics:select-logo',

  /* Takes the Control as a string */
  EditorControlClicked: 'editor:control-clicked',
  /* Takes no parameters */
  EditorFinished: 'editor:finished',
  /* Takes a logo Object */
  EditorSelectLogo: 'editor:select-logo',
  /* Takes a logo Object */
  EditorLoadLogo: 'editor:load-logo',
  /* Takes no parameters */
  EditorCloseModals: 'editor:close-modals',
  /* Takes no parameters */
  EditorHideLoader: 'editor:hide-loader',
  /* Takes a Font ID */
  EditorSelectFont: 'editor:select-font',
  /* Accepts no parameters */
  EditorFetchIdeas: 'editor:fetch-ideas',
  /* Takes a color String */
  EditorSetColorFilter: 'editor:set-color-filter',
  /* Takes payload of { property: String, value: String } */
  EditorSetCustomPalette: 'editor:set-custom-palette',
  /* Takes a String */
  EditorFontsSetInput: 'editor:fonts-set-input',
  /* Takes an Object */
  EditorFontsSetFilter: 'editor:fonts-set-filter',
  /* Takes a String */
  EditorFontsSetTag: 'editor:fonts-set-tag',
  /* Takes a String */
  EditorFontsSetType: 'editor:fonts-set-type',

  EditorIdeasPageOpened: 'editor:ideas-page-opened',
  LogoEditorOpened: 'logo-editor:opened',

  /* Takes a string */
  EditorSetFontNameSearchString: 'editor:font-set-name-search-string',

  /* Accepts no parameters */
  EditorRemoveElement: 'editor:remove-element',

  /* Takes payload of { error: Boolean } */
  SiteTemplateSelected: 'site:template-selected',

  /** Accepts no parameters */
  CartOpenCreditsModal: 'cart:open-credits-modal',
  /**
   * Accepts an Object containing the Package, Stripe Price,
   * and a Promise resolve & reject
   */
  CartOpenUpgradeModal: 'cart:open-upgrade-modal',
  /**
   * Accepts an Object containing the interval, selected package,
   * and Array of mismatched packages
   */
  CartOpenIntervalModal: 'cart:open-interval-modal',
  /** Accepts no parameters */
  CartRefreshCheckoutForm: 'cart:refresh-checkout-form',

  /* Takes the Slug as a string */
  SidebarNavClicked: 'sidebar:nav-clicked',

  /* Takes the progress as a Number */
  StepSetProgress: 'step:set-progress',

  /* Takes no params */
  DashboardRefreshPackage: 'dashboard:refresh-package',
  DashboardRefreshUser: 'dashboard:refresh-user',

  EditorRefreshLogo: 'editor-refresh-logo',
  EditorRefreshMockups: 'editor-refresh-logo',
  EditorNoFontsFound: 'editor-no-fonts-found',
  IdeasHideLoader: 'ideas-hide-loader',
  IdeasIconToggled: 'ideas-icon-toggled',
  IdeasClearFonts: 'ideas-clear-fonts',

  BNGSearched: 'business-name-generator-searched',
  BNGSelected: 'business-name-generator-selected',
  BNGFavourited: 'business-name-generator-favourited',
  BNGClearNames: 'business-name-generator-clear-names',
  LogoShared: 'logo-shared',

  /** Accepts a string */
  CurrencyChange: 'currency:change',

  /** One page site */
  OnePageSiteCreateCustomDomain: 'one-page-site-create-custom-domain',

  /** Fired when the user begins editing a 1ps */
  OnePageSiteEditorVisited: 'one-page-site:editor-visited',

  /** Fired when a user visits their 1ps */
  OnePageSiteVisited: 'one-page-site:visited',

  /** Fired when the user begins editing a site */
  OnePageSiteEdited: 'one-page-site:edited',

  /**
   * Fired when the user publishes a site.
   *
   * Takes { site, links, theme, }
   */
  OnePageSitePublished: 'one-page-site:published',

  PlanCancellationPageOpened: 'plan:cancellation-page-opened',
  PlanCancellationRequested: 'plan:cancellation-requested',
  PlanReactivated: 'plan:reactivated',

  IdeasResetInfiniteLoader: 'ideas:reset-infinite-loader',

  EmailSignatureRemoveProFeatures: 'email-signature:remove-pro-features',
}
